/**
 * Zocdoc admin visual tokens.
 *
 * The page adopts the **behuman-v2** language — Schedule v2's look, page-neutral aliases — so the
 * admin surface reads as part of the same product rather than as a raw ops tool. Values come from
 * `pages/Messages/MESSAGES_V2_DESIGN.md` §7b, which is the canonical list; typography comes from
 * `.behuman-v2-scope` / `.behuman-v2-mulish` in `src/index.css` (Plus Jakarta Sans for text, Mulish
 * for controls).
 *
 * Buttons, fields and section labels are **imported** from `@/components/ui/formModal.tokens` — the
 * app-wide form language already shared by Schedule, Messages and the patient/coverage modals.
 * Nothing is redefined here that already exists there.
 *
 * As in the other token modules, only class strings live here: Tailwind's JIT emits only classes it
 * finds as complete literals, so a component must write `text-[#6B7280]` rather than interpolate a
 * constant.
 */

/** The 18px white panel Schedule uses for its calendar surface. */
// Mirrors `pages/Messages/tokens.ts`' CARD_CLASS. Duplicated rather than imported across pages;
// the pair is a candidate for extraction into `components/ui` once a third page needs it.
export const PANEL =
  'rounded-[18px] border border-[#E7E9EC] bg-white shadow-[0_1px_3px_rgba(16,24,40,.05),0_12px_30px_rgba(16,24,40,.05)]'

/** Page shell: the #F6F7F9 ground the v2 pages sit on. */
export const PAGE = 'behuman-v2-scope min-h-[calc(100vh-54px)] bg-[#F6F7F9] p-4 text-[#1F2733]'

/** Page title — identical to Schedule's `Schedule` h1. */
export const PAGE_TITLE = 'm-0 text-[28px] font-extrabold tracking-[-0.6px]'

/** Panel heading. Smaller and tighter than the page title, per Messages' subject treatment. */
export const PANEL_TITLE = 'text-[17px] font-extrabold tracking-[-0.3px] text-[#1F2733]'
export const PANEL_SUBTITLE = 'text-[13px] leading-relaxed text-[#6B7280]'

/**
 * Inline hint under a control or table.
 *
 * `#6B7280` (4.8:1 on white), not the palette's `#8A929C`/`#9AA0A8` faint tones: those measure
 * 3.2:1 and 2.6:1, which fails WCAG AA for text this size. Everything a hint says here is real
 * information — a UTC caveat, an unverified field, why a button is locked — so it has to be
 * readable rather than decorative.
 */
export const HINT = 'text-[12.5px] text-[#6B7280]'

/** Table chrome: hairline rows on the divider tone, Mulish uppercase headers. */
// Also #6B7280 rather than a faint tone: 11px uppercase is the smallest text on the page and the
// least forgiving place to lose contrast.
export const TABLE_HEAD =
  'behuman-v2-mulish !text-[11px] !font-bold !tracking-[0.06em] !text-[#6B7280] uppercase'
export const TABLE_ROW = 'border-b border-[#ECEEF1]'

/** Monospace id/value chip. Zocdoc ids are the page's main content, so they get a real treatment. */
export const ID_CHIP =
  'rounded-md bg-[#F1F2F4] px-1.5 py-0.5 font-mono text-[12px] text-[#4A5160] break-all'

/**
 * Mantine `Button` prop bundles, so `loading` and `disabled` keep working while the look comes from
 * the shared language. Spread them rather than restyling each call site.
 */
export const PRIMARY_BTN = {
  color: '#E5533D',
  radius: 'md',
  className: 'behuman-v2-mulish !font-bold !text-[13px]',
} as const

export const SECONDARY_BTN = {
  variant: 'default',
  radius: 'md',
  className: 'behuman-v2-mulish !font-bold !text-[13px]',
} as const

/** A write that reaches a third party and cannot be undone by a second call. */
export const DANGER_BTN = {
  color: '#DC2626',
  radius: 'md',
  className: 'behuman-v2-mulish !font-bold !text-[13px]',
} as const

export const SUBTLE_BTN = {
  variant: 'subtle',
  size: 'compact-xs',
  color: 'gray',
  className: 'behuman-v2-mulish !font-bold',
} as const
